import * as React from 'react'
import dynamic from 'next/dynamic'
import ResizeObserver from 'resize-observer-polyfill'

import { useSiteConfig } from '@thg-commerce/enterprise-core'
import {
  pushToEventGA4,
  SortByDataLayerGA4,
} from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import {
  FacetInput,
  ProductSort,
  SimpleFacet,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { spacing, useTheme, ZIndexLevel } from '@thg-commerce/enterprise-theme'
import { CustomDropdown } from '@thg-commerce/gravity-elements'
import { OptionsType } from '@thg-commerce/gravity-elements/CustomDropdown/CustomDropdown'
import { KeyboardKeys } from '@thg-commerce/gravity-theme'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

import { PageOptions } from '../../ProductListPage'
import { BrandFacet } from '../FacetComponents'
import { FacetContent } from '../FacetContent/FacetContent'
import { Facet, FacetDataLayerSchema, FacetsProps } from '../types'
import {
  clearFacetSelection,
  displayFacet,
  isSelectedFacet,
  UpdateFacetAction,
  updateFacetSelection,
} from '../utils'

import {
  ActionFilterButton,
  ButtonContainer,
  FacetContainer,
  HorizontalFacetWrapper,
  StyledBlankDropdown,
  StyledHorizontalFacetContent,
} from './styles'

const CONTAINER_HEIGHT = 100

const Plus = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Plus'),
  { ssr: false, loading: () => null },
)
const Minus = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Minus'),
  { ssr: false, loading: () => null },
)
const SvgIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
  { ssr: false, loading: () => null },
)

interface ClearFacetHandler {
  (facetName: string): () => void
}

export interface HorizontalFacetsProps extends FacetsProps {
  i18nText: {
    searchBrandLabel: string
    searchBrandPlaceholder: string
    filterText: string
    close: string
    apply: string
    clearAll: string
    sortBy: string
    minimumLabel: string
    maximumLabel: string
    sliderHeader: string
  }
  sortTypes: {
    type: ProductSort
    displayText: string
  }[]
  dropDownOptions: OptionsType[]
  hasBrandSearch?: boolean
  selectedSort?: string | number
  facetStyle: {
    facet: {
      width: string
      maxWidth: string
      numberAlignment: HorizontalAlignment
    }
    sort: {
      width: string
    }
    container: {
      gap: {
        row: number
        column: number
      }
    }
  }
  stickyOnDesktop?: boolean
  headerHeight: number
  hideMargin?: boolean
  facetSchema: FacetDataLayerSchema
}

const FilterIcon = ({ icon }) => (
  <div
    style={{
      marginRight: spacing(0.5),
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
    }}
  >
    <SvgIcon
      xmlns="http://www.w3.org/2000/svg"
      viewBox={icon.viewBox}
      width={icon.width}
      height={icon.height}
    >
      <path d={icon.svgPath} fillRule="evenodd" />
    </SvgIcon>
  </div>
)

export const HorizontalFacets = (props: HorizontalFacetsProps) => {
  const theme = useTheme()
  const [showFilteredItems, setShowFilteredItems] = React.useState<boolean>(
    false,
  )
  const containerRef = React.useRef<HTMLDivElement>(null)
  const {
    useGA4EnhancedEcom,
    facetsDisplayedOnProductList,
    enableImmediateFacetUpdate,
  } = useSiteConfig()
  const [facetsSticky, setFacetsSticky] = React.useState(false)
  const [tempSelectedFacets, setTempSelectedFacets] = React.useState(
    props.selectedFacets || [],
  )

  React.useEffect(() => {
    setTempSelectedFacets(props.selectedFacets)
  }, [props.selectedFacets])

  const setTempFacets = React.useCallback(
    (pageOptions: PageOptions) => {
      setTempSelectedFacets(pageOptions.facets || [])
    },
    [props.selectedFacets],
  )

  const trackingEventRef = React.useRef<HTMLHeadingElement>(null)

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (elements, _) => {
        const targetElement = elements[0]
        if (targetElement && targetElement.isIntersecting) {
          setFacetsSticky(false)
        } else {
          setFacetsSticky(true)
        }
      },
      {
        rootMargin: `-${props.headerHeight + 1}px 0px 0px 0px`,
        threshold: 1,
      },
    )
    trackingEventRef.current && observer.observe(trackingEventRef.current)
  }, [props.headerHeight])
  const [focusIndex, setFocusIndex] = React.useState(0)

  const selectedFacets = enableImmediateFacetUpdate
    ? props.selectedFacets
    : tempSelectedFacets

  const createSelectDropdownOptionHandler = React.useCallback(
    (facet, facetOptions) => {
      return (event?: React.KeyboardEvent) => {
        if (event && 'key' in event) {
          if (
            event.key !== KeyboardKeys.Enter &&
            event.key !== KeyboardKeys.Spacebar
          ) {
            return
          }
          event.preventDefault()
        }
        const isSelected = isSelectedFacet(
          facet.facetName,
          facetOptions[focusIndex],
          selectedFacets,
        )

        const newFacets = updateFacetSelection(
          isSelected
            ? UpdateFacetAction.RemoveFacet
            : UpdateFacetAction.AddFacet,
          facet.facetName,
          facet.facetHeader,
          { optionName: facetOptions[focusIndex].optionName },
          tempSelectedFacets,
          useGA4EnhancedEcom,
          props.facetSchema,
        )
        setTempFacets(newFacets)
      }
    },
    [
      focusIndex,
      props.facetSchema,
      useGA4EnhancedEcom,
      selectedFacets,
      setTempFacets,
      tempSelectedFacets,
    ],
  )

  const handleDropdownClose = React.useCallback(() => {
    props.onInputChange({ facets: tempSelectedFacets })
    setTempSelectedFacets(props.selectedFacets)
  }, [props.onInputChange, tempSelectedFacets, props.selectedFacets])

  const createClearFacetHandler: ClearFacetHandler = React.useCallback(
    (facetName) => {
      return () => {
        props.onInputChange({
          facets: clearFacetSelection(facetName, props.selectedFacets),
        })
        setTempSelectedFacets([])
      }
    },
    [props.onInputChange, props.selectedFacets],
  )

  const facets = React.useMemo(() => {
    return props.facets
      .filter(
        (facet) =>
          facet.__typename === Facet.SLIDER ||
          ((facet.__typename === Facet.SIMPLE ||
            facet.__typename === Facet.RANGED) &&
            displayFacet(facet.options)),
      )
      .map((facet) => {
        const sectionCount = props.selectedFacets.find(
          (selection) => selection.facetName === facet.facetName,
        )?.selections.length

        const brandContent = facet.facetName.includes('brand_content')
        const facetOptions = facet.options
        const selectHandler = createSelectDropdownOptionHandler(
          facet,
          facetOptions,
        )
        const clearHandler = createClearFacetHandler(facet.facetName)

        return (
          <StyledBlankDropdown
            key={facet.facetName}
            quantifier={sectionCount}
            zIndex={ZIndexLevel.Base5}
            width={props.facetStyle.facet.width}
            maxWidth={props.facetStyle.facet.maxWidth}
            isBrandFacet={brandContent}
            dropdownWidth={brandContent ? '1000px' : undefined}
            useDefaultDropdownWidth={!brandContent}
            buttonText={facet.facetHeader}
            disabledClearAll={sectionCount === undefined}
            ariaHasPopup="listbox"
            onCustomButtonClick={() => clearHandler()}
            onClose={() => {
              handleDropdownClose()
            }}
            i18nButtonText={{
              ...props.i18nText,
              customButton: props.i18nText.clearAll,
              close: enableImmediateFacetUpdate
                ? props.i18nText.close
                : props.i18nText.apply,
            }}
            hasBrandSearch={props.hasBrandSearch}
            badgePosition={props.facetStyle?.facet.numberAlignment}
            iconOverride={theme?.icons?.chevronDown}
            focusIndex={focusIndex}
            setFocusIndex={setFocusIndex}
            optionsLength={facetOptions?.length || 0}
            selectDropdownOption={(event?: React.KeyboardEvent) => {
              selectHandler(event as React.KeyboardEvent)
            }}
            facetName={facet.facetName}
          >
            {facet.__typename === Facet.SIMPLE && brandContent ? (
              <BrandFacet
                {...props}
                facet={facet as SimpleFacet}
                hasBrandSearch={true}
                useGA4EnhancedEcom={useGA4EnhancedEcom}
                facetSchema={props.facetSchema}
              />
            ) : (
              <FacetContent
                isHorizontalFacet
                facet={facet}
                maxHeight={
                  theme.widget.productList?.FacetContent.container
                    .facetContentHeight
                }
                selectedFacets={
                  enableImmediateFacetUpdate
                    ? props.selectedFacets
                    : tempSelectedFacets
                }
                onInputChange={
                  enableImmediateFacetUpdate
                    ? props.onInputChange
                    : setTempFacets
                }
                i18nText={props.i18nText}
                useGA4EnhancedEcom={useGA4EnhancedEcom}
                facetSchema={props.facetSchema}
              />
            )}
          </StyledBlankDropdown>
        )
      })
  }, [
    props,
    focusIndex,
    theme?.icons?.chevronDown,
    useGA4EnhancedEcom,
    createSelectDropdownOptionHandler,
    createClearFacetHandler,
    enableImmediateFacetUpdate,
    setTempFacets,
    tempSelectedFacets,
    handleDropdownClose,
    theme?.widget?.productList?.FacetContent?.container?.facetContentHeight,
  ])

  const [showFilterButton, setShowFilterButton] = React.useState<boolean>(
    !!facetsDisplayedOnProductList &&
      facets.length >= facetsDisplayedOnProductList,
  )

  React.useEffect(() => {
    if (!containerRef?.current) {
      return
    }
    let resizeObserver: ResizeObserver | undefined
    resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const height = entries[0]?.contentRect.height || 0
      if (showFilterButton !== height > CONTAINER_HEIGHT) {
        setShowFilterButton(height > CONTAINER_HEIGHT)
      }
    })
    resizeObserver?.observe(containerRef.current)

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [facets?.length, showFilterButton])

  const icons = theme.icons
  const themeIcons = icons.plus && icons.minus

  return (
    <HorizontalFacetWrapper
      isAlignTop={showFilterButton}
      stickyOnDesktop={props.stickyOnDesktop}
      scrolled={facetsSticky}
      hideMargin={props.hideMargin}
      ref={trackingEventRef}
    >
      <StyledHorizontalFacetContent
        data-testid="horizontal-facet-wrapper"
        showFilteredItems={showFilteredItems}
      >
        <FacetContainer
          data-testid="horizontal-facet-dropdowns-container"
          ref={containerRef}
          gap={props.facetStyle.container.gap}
        >
          <CustomDropdown
            addEntryKey={true}
            stickyPosition
            zIndex={ZIndexLevel.Base5}
            placeholder={props.i18nText.sortBy}
            aria-label={props.i18nText.sortBy}
            data-testid="sort-by-dropdown"
            options={props.dropDownOptions}
            useDefaultDropdownWidth
            width={props.facetStyle.sort.width}
            height={
              theme.widget.productList?.horizontalFacets?.container
                ?.facetWrapperHeight
            }
            onChange={(event) => {
              const sort = props.sortTypes.find(
                (sort) => sort.type === event.key,
              )?.type
              props.onInputChange({
                sort,
              })

              useGA4EnhancedEcom &&
                pushToEventGA4<SortByDataLayerGA4>({
                  event: 'sort_by',
                  sort_method: sort || '',
                })
            }}
            selected={props.selectedSort}
            hasPlaceholderAsLabel
            iconOverride={theme?.icons?.chevronDown}
          />
          {facets}
        </FacetContainer>
      </StyledHorizontalFacetContent>

      <ButtonContainer>
        {props.selectedFacets.length > 0 && (
          <ActionFilterButton
            emphasis="low"
            onClick={() => {
              props.onInputChange({
                facets: [],
              })
              setTempSelectedFacets([])
              setShowFilteredItems(false)
            }}
            data-testid="clear-all-button"
          >
            {props.i18nText.clearAll}
          </ActionFilterButton>
        )}
        {showFilterButton && (
          <ActionFilterButton
            emphasis="low"
            onClick={() => setShowFilteredItems((state) => !state)}
            data-testid="filter-button"
            tabIndex={-1}
          >
            {themeIcons ? (
              showFilteredItems ? (
                <FilterIcon icon={icons.minus} />
              ) : (
                <FilterIcon icon={icons.plus} />
              )
            ) : showFilteredItems ? (
              <Minus />
            ) : (
              <Plus />
            )}
            {props.i18nText.filterText}
          </ActionFilterButton>
        )}
      </ButtonContainer>
    </HorizontalFacetWrapper>
  )
}
